<template>
	<main class="relative 2xl:px-80 xl:px-32 lg:px-12 px-4">
		<section
			class="relative flex flex-col-reverse lg:flex-row items-center lg:pt-20 mb-20 max-w-screen-3xl mx-auto"
		>
			<div class="flex flex-col justify-between w-full">
				<div>
					<h1 class="text-left font-bold font-georama text-4xl mb-0">hokify</h1>
					<h2
						class="text-left font-bold font-georama sm:text-3xl text-2xl text-text-regular-default pt-4"
					>
						Die mobile Job & Candidate Plattform.
					</h2>
					<p
						class="mb-1 md:mb-4 max-w-lg xl:max-w-xl text-text-regular-default font-paragraph-regular-size font-paragraph-regular-line-height font-paragraph-regular-weight font-paragraph-regular-family"
					>
						Jobsuche & Bewerbung einfach gemacht. Finde innerhalb weniger Minuten deinen Traumjob!
					</p>
					<p
						class="text-text-regular-default font-inline-bold-size font-inline-bold-line-height font-inline-bold-weight font-inline-bold-family"
					>
						Mobil, direkt und einfach!
					</p>
				</div>
				<div class="w-full">
					<div class="pt-8 sm:pt-16 flex flex-col">
						<p class="mb-8 text-xl lg:text-2xl text-text-regular-default font-bold">
							Wähle dein Land für Jobangebote!
						</p>
						<div class="w-full flex sm:justify-start flex-wrap">
							<div
								class="h-32 lg:h-44 flex flex-col justify-around items-center text-blue-grey mx-6"
							>
								<nuxt-link
									to="https://hokify.at"
									class="inline-block"
									@click="saveCountryPreference('at')"
								>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										viewBox="0 0 600 600"
										class="w-24 rounded-full"
									>
										<rect fill="#f00" width="600" height="600"></rect>
										<rect fill="#fff" y="200" width="600" height="200"></rect>
									</svg>
								</nuxt-link>
								<span class="block text-text-regular-default font-bold">Österreich</span>
							</div>
							<div
								class="h-32 lg:h-44 lg:w-36 flex flex-col justify-around items-center text-blue-grey mx-6"
							>
								<nuxt-link
									to="https://hokify.de"
									class="inline-block"
									@click="saveCountryPreference('de')"
								>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										viewBox="0 0 3 3"
										class="w-24 rounded-full"
									>
										<rect width="3" height="3" y="0" x="0" fill="#000"></rect>
										<rect width="3" height="2" y="1" x="0" fill="#D00"></rect>
										<rect width="3" height="1" y="2" x="0" fill="#FFCE00"></rect>
									</svg>
								</nuxt-link>
								<span class="block text-text-regular-default font-bold">Deutschland</span>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div class="lg:pl-10 md:w-1/2">
				<!-- Keep this dynamic so Nuxt serves the public asset directly in dev instead of virtualizing it. -->
				<img
					class="max-w-[200px] xl:max-w-[580px] lg:max-w-[500px]"
					:src="'/landing-top.avif'"
					alt=""
				/>
			</div>
		</section>
		<section class="mb-10 max-w-screen-3xl mx-auto">
			<div class="flex h-6">
				<div class="flex-1 rounded-l-full bg-blue-cyan-500">&nbsp;</div>
				<div class="flex-1 bg-yellow-mustard-500">&nbsp;</div>
				<div class="flex-1 bg-blue-cyan-300">&nbsp;</div>
				<div class="flex-1 rounded-r-full bg-green-green-500">&nbsp;</div>
			</div>
		</section>
		<section class="pt-8 pb-12 sm:pt-10 sm:pb-20 mb-3 max-w-screen-3xl mx-auto">
			<div>
				<div class="text-center flex justify-center flex-wrap">
					<nuxt-link
						v-for="logo in marketingLogos"
						:key="logo.imgSource"
						:to="logo.imgLink"
						class="mt-4 sm:mt-3 mx-3 lg:mx-2 xl:mx-3 relative inline-block"
					>
						<img
							:src="getAssetSrc(logo.imgSource)"
							:alt="logo.imgAlt"
							class="inline-block h-6 align-middle"
							style="max-width: 110px"
						/>
					</nuxt-link>
				</div>
			</div>
		</section>
		<section class="w-[100vw] relative 2xl:-left-80 xl:-left-32 lg:-left-12 -left-3">
			<CallToAction styling="international-banner" to="mailto:info@hokify.com" class="">
				<template #headline>
					<h2 class="font-georama sm:text-3xl text-2xl text-text-regular-on-dark font-bold">
						Du möchtest mehr über hokify erfahren?
					</h2>
					<p class="text-base mb-0">
						Wir würden uns freuen, dich über die Möglichkeiten von hokify zu informieren.
					</p>
				</template>
				<template #button> Schreib uns! </template>
			</CallToAction>
		</section>
		<section class="text-center py-12 sm:py-20 max-w-screen-3xl mx-auto">
			<div class="mb-10 sm:mb-12">
				<h3 class="mb-6 text-text-regular-default text-2xl text-bold">
					{{
						`Über ${marketingValues && marketingValues.publishingUsers} Unternehmen vertrauen seit 2015 auf hokify`
					}}
				</h3>
			</div>

			<div class="flex flex-wrap flex-row">
				<div
					v-for="(logo, index) in customerLogos"
					:key="index"
					class="w-1/2 sm:w-1/4 md:w-1/6 h-16 px-4 relative"
				>
					<img
						:src="getAssetSrc(logo.imgSource)"
						:alt="logo.imgAlt"
						class="inline-block center-in-parent w-auto object-contain max-w-[100px] max-h-[48px]"
					/>
				</div>
			</div>
		</section>
		<div class="relative w-screen 2xl:-left-80 xl:-left-32 lg:-left-12 -left-3 top-20">
			<div class="h-60 w-full lg:bg-main absolute hidden lg:block" />
		</div>
		<section class="lg:mb-48 mb-20 flex justify-center max-w-screen-3xl mx-auto gap-20">
			<picture class="object-contain w-full lg:min-w-[580px] lg:max-w-[580px] z-10">
				<source :srcset="'/hokify-team.avif'" />
				<img :src="'/hokify-team.png'" alt="" />
			</picture>
			<img
				class="min-w-96 py-20 pt-24 lg:block hidden z-10"
				:src="'/hokify-slogan.svg'"
				alt="hokify slogan"
			/>
		</section>

		<section class="mb-32 flex lg:flex-row flex-col-reverse gap-6 max-w-screen-3xl mx-auto">
			<div class="lg:w-1/2 flex flex-col">
				<h3 class="text-xl font-bold">Was ist hokify?</h3>
				<h4 class="font-georama text-2xl lg:text-3xl text-text-regular-default">
					Mehr als eine Job Platform!
				</h4>
				<p class="text-base text-text-regular-default">
					Egal ob in der Logistik, im Einzelhandel, in der Mechanik oder in der Pflege. Mit über 17
					Branchen deckt hokify ein breites Spektrum an Berufen ab und bietet täglich neue
					Stellenangebote für dich! Mit nur einem Profil kannst du dich auf alle Jobs bei hokify
					bewerben. Der gesamte Bewerbungsprozess läuft direkt über hokify schnell, unkompliziert
					und mobil. Damit findest den passenden Job in kürzester Zeit.
				</p>
				<p class="text-base text-text-regular-default">
					Über 65.000 Unternehmen in Deutschland und Österreich vertrauen bereits auf hokify!
					Überzeuge dich selbst:&nbsp;
				</p>
				<div class="flex flex-col gap-y-4 lg:flex-row lg:gap-x-4">
					<HyButton color="primary" to="https://hokify.at/">Jobsuche Österreich</HyButton>
					<HyButton color="primary" to="https://hokify.de/?hsLang=de-at"
						>Jobsuche Deutschland</HyButton
					>
				</div>
			</div>
			<div class="lg:w-1/2">
				<img :src="'/media.png'" alt="" class="w-full object-contain" />
			</div>
		</section>
		<section class="mb-20 flex gap-6 lg:flex-row flex-col max-w-screen-3xl mx-auto">
			<div class="lg:w-1/2">
				<img :src="'/do-it-jobs.png'" alt="" class="w-full object-contain" />
			</div>
			<div class="lg:w-1/2 flex flex-col">
				<h3 class="text-xl font-bold">2/3 der Arbeitnehmenden arbeiten in</h3>
				<h4 class="font-georama text-2xl lg:text-3xl text-text-regular-default">Do-it-Jobs</h4>
				<ul class="list-disc list-inside">
					<li class="pb-4 text-text-regular-default">
						Do-it-Jobs sind die Jobs, die den Laden am Laufen halten.
					</li>
					<li class="pb-4 text-text-regular-default">
						Die Jobs, die wir alle tagtäglich brauchen.
					</li>
					<li class="pb-4 text-text-regular-default">
						Die Jobs, ohne die unsere Wirtschaft und Gesellschaft nicht funktionieren würde.
					</li>
				</ul>
				<p class="text-base text-text-regular-default">
					Es sind die Jobs, die oft hinter den Kulissen dafür sorgen, dass der Alltag reibungslos
					abläuft. Do-it-Jobs umfassen ein breites Spektrum an Berufen, darunter Blue Collar, Grey
					Collar, Pink Collar und Basic White Collar Jobs. Ob es die handwerklichen und technischen
					Aufgaben sind, die unsere Infrastruktur am Laufen halten, die Dienstleistungen, die wir
					tagtäglich in Anspruch nehmen, oder die betriebswirtschaftlichen Tätigkeiten, auf die
					Unternehmen angewiesen sind – <b>Do-it-Jobs sind unverzichtbar</b>.
				</p>
				<div class="flex flex-col gap-y-4 lg:flex-row lg:gap-x-4">
					<HyButton color="primary" to="https://recruiting.hokify.at/"
						>Recruiting Österreich</HyButton
					>
					<HyButton color="primary" to="https://recruiting.hokify.de/?hsLang=de-at"
						>Recruiting Deutschland</HyButton
					>
				</div>
			</div>
		</section>
		<section class="mx-8 max-w-screen-3xl mx-auto">
			<h3
				class="font-georama sm:text-3xl text-2xl font-bold text-center lg:mb-20 mb-10 text-blue-grey"
			>
				Häufige Fragen
			</h3>
			<div class="max-w-3xl">
				<div class="mb-10">
					<h4 class="text-2xl text-text-regular-default">Ist hokify kostenlos?</h4>
					<p class="text-base text-text-regular-default">
						Keine Sorge – die Jobsuche bei uns ist für dich völlig kostenfrei
					</p>
				</div>
				<div class="mb-10">
					<h4 class="text-2xl text-text-regular-default">Wie kann ich mich bewerben?</h4>
					<p class="text-base text-text-regular-default">
						Sobald du dein Profil erstellt hast, kannst du dich mit wenigen Klicks auf alle Jobs bei
						hokify bewerben – egal ob in der App (iOS oder Android) oder im Browser.Starte deine
						Bewerbung, indem du auf <b>„Jetzt bewerben“</b> klickst (z. B. in den Jobvorschlägen)
						oder in der App einen passenden Job nach rechts swipest. Bitte beachte: Bewerbungen per
						E-Mail können nicht berücksichtigt werden.
					</p>
				</div>
				<div class="mb-20">
					<h4 class="text-2xl text-text-regular-default">
						Wie lange dauert eine Bewerbung bei hokify?
					</h4>
					<p class="text-base text-text-regular-default">
						Ist dein Profil einmal angelegt, brauchst du nur wenige Minuten für die Jobsuche &
						Bewerbung!
					</p>
				</div>
			</div>
		</section>
	</main>
</template>

<script lang="ts">
import {
	getCustomerLogos,
	getMarketingLogos
} from '@hokify/shared-components-nuxt3/lib/helpers/logos';
import CallToAction from '@hokify/shared-components-nuxt3/lib/components/CallToAction.vue';
import { saveCountryPreference } from '../helpers/preferences';
import hokifyTeamUrl from '~/assets/img/hokify-team.jpg';
import explanationVisualB2BUrl from '~/assets/img/explanation-visual-b2b.jpg';
import { defineNuxtComponent } from '#imports';

export default defineNuxtComponent({
	name: 'StartPage',
	components: {
		CallToAction
	},
	async asyncData({ req }) {
		const config = useRuntimeConfig();
		const { baseURL } = config.public;
		const { data: marketingValues } = await useFetch(
			`${baseURL}/website-api/general/marketing-values`
		);
		let locale = 'de';

		if (process.client) {
			const newLocale = window.navigator?.language?.split('-')[0];
			if (newLocale !== 'de') {
				locale = 'en';
			}
		} else if (req?.headers['accept-language']) {
			const newLocale = req.headers['accept-language']
				.split(',')[0]
				.toLocaleLowerCase()
				.substring(0, 2);
			if (newLocale !== 'de') {
				locale = 'en';
			}
		}

		return {
			locale,
			marketingValues: marketingValues.value
		};
	},
	data() {
		const hokifyTeam = hokifyTeamUrl;
		const explanationVisualB2B = explanationVisualB2BUrl;
		const marketingValues = undefined as
			| undefined
			| {
					publishingUsers: string;
					monthlyUsers: string;
					candidatesPlaced: string;
					averageSecondsApplication: string;
					expirationDate: number;
			  };

		return {
			hokifyTeam,
			explanationVisualB2B,
			marketingValues,
			customerLogos: getCustomerLogos('de'),
			marketingLogos: getMarketingLogos('de').concat(getMarketingLogos('at'))
		};
	},
	methods: {
		saveCountryPreference,
		// works - but performance might be bad?
		// how can we access @shared-components without accessing node_modules?
		getAssetSrc(name: string) {
			const path = `../../node_modules/@hokify/shared-components-nuxt3/assets/svgs/logos/${name}`;
			const modules = import.meta.glob(
				'../../node_modules/@hokify/shared-components-nuxt3/assets/svgs/logos/**/*',
				{ eager: true }
			) as Record<string, { default: string }>;

			if (modules[path]) {
				return modules[path].default;
			}
			return '';
		}
	},
	computed: {
		averageMinutesApplication(): number | null {
			const averageSecondsApplication =
				this.marketingValues?.averageSecondsApplication &&
				Number.parseInt(this.marketingValues.averageSecondsApplication, 10);
			if (!averageSecondsApplication || Number.isNaN(averageSecondsApplication)) {
				return null;
			}

			return averageSecondsApplication / 60;
		}
	}
});
</script>

<style scoped lang="scss">
.center-in-parent {
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
}

.font-georama {
	font-family: var(--font-family-serif);
}
</style>
